/**
 * OAUTH ONBOARDING MODAL
 * 
 * Shows a modal for OAuth users (Google) who need to accept terms and set marketing preferences.
 * This modal appears when a user signs in via OAuth and hasn't accepted terms yet.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Loader2, CheckCircle } from 'lucide-react';

interface OAuthOnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  userId: string;
}

export function OAuthOnboardingModal({ isOpen, onComplete, userId }: OAuthOnboardingModalProps) {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleComplete = async () => {
    if (!acceptTerms) {
      toast({
        title: 'Terms acceptance required',
        description: 'Please accept the Terms and Conditions to continue.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      
      // Update user metadata with terms acceptance
      await supabase.auth.updateUser({
        data: {
          terms_accepted: true,
          terms_accepted_at: now,
          marketing_consent: acceptMarketing,
        },
      });

      // Update profile with marketing consent
      const { error } = await supabase.from('profiles').update({
        marketing_consent: acceptMarketing,
        marketing_consent_date: acceptMarketing ? now : null,
      }).eq('id', userId);

      if (error) throw error;

      toast({
        title: '✅ Welcome to ListBright!',
        description: 'Your account is all set up.',
      });

      onComplete();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <DialogTitle className="text-center font-display text-2xl">
            Almost there!
          </DialogTitle>
          <DialogDescription className="text-center">
            Just a quick step to complete your account setup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Terms and Conditions */}
          <div className="flex items-start gap-3">
            <Checkbox 
              id="oauth-terms" 
              checked={acceptTerms}
              onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
              className="mt-0.5"
            />
            <Label htmlFor="oauth-terms" className="text-sm leading-relaxed cursor-pointer">
              I agree to the{' '}
              <Link to="/terms" className="text-primary hover:underline font-medium" target="_blank">
                Terms and Conditions
              </Link>
              , including the requirements to disclose AI-enhanced photos and virtually staged images in my property listings.
            </Label>
          </div>

          {/* Marketing Consent */}
          <div className="flex items-start gap-3">
            <Checkbox 
              id="oauth-marketing" 
              checked={acceptMarketing}
              onCheckedChange={(checked) => setAcceptMarketing(checked as boolean)}
              className="mt-0.5"
            />
            <Label htmlFor="oauth-marketing" className="text-sm text-muted-foreground cursor-pointer">
              I would like to receive marketing communications, product updates, and real estate industry tips from ListBright. (Optional)
            </Label>
          </div>

          <Button 
            onClick={handleComplete} 
            className="w-full" 
            variant="hero"
            disabled={!acceptTerms || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue to ListBright'
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            You can update your marketing preferences anytime in your account settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
